import { apiErrorReason } from '@/lib/api';

import type { CodeProposal } from './types';
import { errorAdvice } from '@/lib/error-advice';

// Código propuesto en el alta (G3-2, 04-GC-P08 §B.1). Puro: traduce la respuesta o el
// rechazo de GET /quality/documents/code-proposal a lo que el usuario debe hacer. El
// servidor es la autoridad del patrón y del consecutivo; acá solo se presenta.

/** Tabla de módulos SAP de 04-GC-P08 (la misma del API, code-pattern.ts). */
export const SAP_MODULES: readonly { code: string; label: string }[] = [
  { code: 'CP', label: 'Compras-Proveedores' },
  { code: 'CR', label: 'CRM' },
  { code: 'FI', label: 'Finanzas' },
  { code: 'GE', label: 'Gestión' },
  { code: 'GB', label: 'Gestión de Bancos' },
  { code: 'IF', label: 'Informes' },
  { code: 'IN', label: 'Inventarios' },
  { code: 'MR', label: 'MRP' },
  { code: 'OP', label: 'Oportunidades' },
  { code: 'PR', label: 'Producción' },
  { code: 'RE', label: 'Recursos' },
  { code: 'RH', label: 'Recursos Humanos' },
  { code: 'SE', label: 'Servicio' },
  { code: 'SN', label: 'Socios de Negocios' },
  { code: 'VC', label: 'Ventas-Clientes' },
  { code: 'OT', label: 'Otros' },
];

export type ProposalView =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'proposed'; scheme: 'proceso' | 'sap'; code: string }
  | { kind: 'free' }
  | { kind: 'needs_sap' }
  /** `allowManual`: el patrón no se puede saber (proceso sin tipo), pero el código se
   *  puede escribir a mano (el API lo acepta sin patrón, revisión adversarial M2). */
  | { kind: 'blocked'; message: string; toCatalogs: boolean; allowManual: boolean }
  | { kind: 'error'; message: string };

export interface ProposalState {
  /** Tipo y proceso elegidos. */
  ready: boolean;
  data: CodeProposal | undefined;
  error: unknown;
  isLoading: boolean;
}

const UNAVAILABLE: Record<string, string> = {
  invalid_document_type: 'El tipo de documento ya no está disponible. Elige otro.',
  invalid_process_area: 'El proceso ya no está disponible. Elige otro.',
};
// Se arreglan en Catálogos; el mensaje del servidor nombra el proceso a configurar.
const FIX_IN_CATALOGS = new Set(['process_type_missing', 'process_tree_invalid']);

export function proposalView(s: ProposalState): ProposalView {
  if (!s.ready) {
    return { kind: 'idle' };
  }
  if (s.error !== null && s.error !== undefined) {
    return rejectionView(s.error);
  }
  if (s.isLoading || s.data === undefined) {
    return { kind: 'loading' };
  }
  if (s.data.scheme === 'libre' || s.data.code === null) {
    return { kind: 'free' };
  }
  return { kind: 'proposed', scheme: s.data.scheme, code: s.data.code };
}

function rejectionView(err: unknown): ProposalView {
  const reason = apiErrorReason(err);
  if (reason === 'sap_module_required') {
    return { kind: 'needs_sap' };
  }
  if (reason !== undefined && FIX_IN_CATALOGS.has(reason) && err instanceof Error) {
    return {
      kind: 'blocked',
      message: err.message,
      toCatalogs: true,
      allowManual: reason === 'process_type_missing',
    };
  }
  // Se eligió el tipo de proceso (ESTRATÉGICOS) y no el proceso: el mensaje del servidor
  // nombra el grupo; se arregla eligiendo otro proceso en el mismo formulario.
  if (reason === 'process_group_selected' && err instanceof Error) {
    return { kind: 'blocked', message: err.message, toCatalogs: false, allowManual: false };
  }
  const unavailable = reason === undefined ? undefined : UNAVAILABLE[reason];
  if (unavailable !== undefined) {
    return { kind: 'blocked', message: unavailable, toCatalogs: false, allowManual: false };
  }
  return {
    kind: 'error',
    message: `No se pudo calcular el código. ${errorAdvice(err)}`,
  };
}
